import { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'all';
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');
  const [selectedCategory, setSelectedCategory] = useState<string>(slug || 'all');

  useEffect(() => {
    setSelectedCategory(slug || 'all');
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = [...comics];

    if (selectedCategory && selectedCategory !== 'all') {
      const cat = categories.find((c) => c.slug === selectedCategory);
      if (cat) {
        result = result.filter((c) => c.categoryId === cat.id);
      }
    }

    if (tabParam === 'hot') {
      result = result.filter((c) => c.views > 600000);
    }

    switch (sortBy) {
      case 'popular':
        result.sort((a, b) => b.views - a.views);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'latest':
      default:
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
    }

    return result;
  }, [selectedCategory, sortBy, tabParam]);

  const currentCategoryName = selectedCategory !== 'all'
    ? categories.find((c) => c.slug === selectedCategory)?.name || '分类浏览'
    : '全部漫画';

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `韩漫画网 - ${currentCategoryName}`,
            description: `韩漫画网${currentCategoryName}分类，下拉式完整版韩漫免费阅读，无删减漫画大全。`,
            url: `http://uure9.cn/category${selectedCategory !== 'all' ? `/${selectedCategory}` : ''}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '韩漫画网',
              url: 'http://uure9.cn/',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'http://uure9.cn/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: currentCategoryName,
                  item: `http://uure9.cn/category${selectedCategory !== 'all' ? `/${selectedCategory}` : ''}`,
                },
              ],
            },
          }),
        }}
      />

      <div className="pt-20 md:pt-24 pb-16 md:pb-24 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-600">{currentCategoryName}</span>
          </nav>

          <div className="mb-10">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
              {currentCategoryName}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl">
              {selectedCategory !== 'all'
                ? categories.find((c) => c.slug === selectedCategory)?.description
                : '浏览韩漫画网全部漫画资源，下拉式完整版免费阅读，无删减内容每日更新。'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/category"
                className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  selectedCategory === 'all'
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                全部
              </Link>
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    selectedCategory === cat.slug
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-foreground-400 text-xs whitespace-nowrap">排序:</span>
              {([
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '最热' },
                { key: 'rating', label: '评分' },
              ] as const).map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-foreground-400 hover:text-foreground-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filteredComics.map((comic) => (
              <Link
                key={comic.id}
                to={`/comic/${comic.id}`}
                className="group rounded-xl overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
              >
                <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={comic.cover}
                    alt={comic.title}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      comic.status === '连载中'
                        ? 'bg-accent-500/90 text-primary-950'
                        : 'bg-secondary-500/90 text-background-50'
                    }`}>
                      {comic.status}
                    </span>
                  </div>
                </div>
                <div className="p-3">
                  <h3 className="text-foreground-900 font-medium text-sm group-hover:text-primary-600 transition-colors duration-200 truncate">
                    {comic.title}
                  </h3>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-foreground-400 text-xs">
                      {comic.author}
                    </span>
                    <span className="flex items-center gap-0.5 text-accent-500 text-xs">
                      <i className="ri-star-fill text-[10px]"></i>
                      {comic.rating}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-book-2-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-foreground-400 text-sm">暂无该分类的漫画</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}